import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  LinearProgress,
} from '@material-ui/core';
import { useApi, discoveryApiRef, identityApiRef, alertApiRef } from '@backstage/core-plugin-api';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  parentFolderId: string | null;
}

export const CreateFolderDialog = ({ open, onClose, onCreated, parentFolderId }: Props) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);

  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleClose = () => {
    if (!creating) {
      setName('');
      onClose();
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      alertApi.post({ message: 'Please enter a folder name.', severity: 'warning' });
      return;
    }

    setCreating(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: name.trim(),
          parent_id: parentFolderId,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Failed to create folder');
      }

      alertApi.post({ message: `Folder "${name.trim()}" created.`, severity: 'success' });
      setName('');
      onCreated();
      onClose();
    } catch (err: any) {
      alertApi.post({ message: `Failed to create folder: ${err.message}`, severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Folder</DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
        {creating && <LinearProgress />}
        <TextField
          label="Folder Name *"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={creating}
          variant="outlined"
          size="small"
          fullWidth
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button onClick={handleCreate} color="primary" variant="contained" disabled={creating || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
